import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Echo from "laravel-echo";
import Pusher from "pusher-js";
import { requestGet, requestPost } from "../config/Api";
import { useAuth } from "./AuthContext";
import { useCallback } from "react";

const NotificationContext = createContext();
export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const { auth } = useAuth();

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // evita que una respuesta vieja pise una nueva
  const lastLoadIdRef = useRef(0);

  const sortDesc = (arr) =>
    [...arr].sort((a, b) => {
      const ta = new Date(a.created_at).getTime();
      const tb = new Date(b.created_at).getTime();
      return tb - ta;
    });

  const loadNotifications = useCallback(async () => {
    const loadId = ++lastLoadIdRef.current;
    setLoading(true);

    try {
      const res = await requestGet("/notifications");

      if (loadId !== lastLoadIdRef.current) return;

      const sorted = sortDesc(res.data);
      setNotifications(sorted);
      setUnreadCount(sorted.filter((n) => !n.read_at).length);
    } finally {
      if (loadId === lastLoadIdRef.current) setLoading(false);
    }
  }, []);

  const markAllAsRead = async () => {
    await requestPost("/notifications/read");

    // optimista
    setNotifications((prev) =>
      prev.map((n) => ({
        ...n,
        read_at: n.read_at || new Date().toISOString(),
      })),
    );
    setUnreadCount(0);
  };

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    if (!auth.user) return;

    loadNotifications();

    window.Pusher = Pusher;

    window.Echo = new Echo({
      broadcaster: "reverb",
      key: process.env.REACT_APP_REVERB_APP_KEY,
      wsHost: process.env.REACT_APP_REVERB_HOST,
      wsPort: process.env.REACT_APP_REVERB_PORT,
      forceTLS: process.env.REACT_APP_REVERB_SCHEME === "https",
      disableStats: true,
      enabledTransports: ["ws", "wss"],
      authEndpoint:
        process.env.REACT_APP_NOTIFICATION_URL + "broadcasting/auth",
      auth: { headers: { Authorization: `Bearer ${auth.token}` } },
    });

    window.Echo.private(`App.Models.User.${auth.user.id}`).notification(
      async (payload) => {
        // desktop notification
        if (Notification.permission === "granted") {
          new Notification("Nueva notificación", {
            body: payload?.data?.message ?? "Tenés una nueva notificación",
            icon: "/logo192.png",
          });
        }

        // ✅ Insertá arriba inmediatamente (evita que “quede al final”)
        // payload de Echo trae {id, type, data, created_at...} a veces no viene todo.
        // Creamos una “notificación local” mínima.
        const nowIso = new Date().toISOString();
        const incoming = {
          id: payload.id || `local-${nowIso}`,
          data: payload.data,
          read_at: null,
          created_at: payload.created_at || nowIso,
          updated_at: payload.updated_at || nowIso,
          type: payload.type,
        };

        setNotifications((prev) => {
          // evitar duplicados
          const exists = prev.some((n) => n.id === incoming.id);
          const next = exists ? prev : [incoming, ...prev];
          return sortDesc(next).slice(0, 50);
        });

        setUnreadCount((c) => c + 1);

        // ✅ Reconciliar contra DB (verdad)
        // pero sin pisadas por race conditions (gracias al lastLoadIdRef)
        await loadNotifications();
      },
    );

    return () => {
      window.Echo?.disconnect();
    };
  }, [auth.user, auth.token, loadNotifications]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        markAllAsRead,
        reload: loadNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
